import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PaymentsServiceApi } from '../services/PaymentsServiceApi';
import { PaymentsApi } from '../services/ApiService';
import { useSubscription, getUserTier, isFreeTier, isPaidTier } from '../contexts/SubscriptionContext';
import { isPaymentsEnabled } from '../config/featureFlags';
import { STRIPE_ENABLED, STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import { loadStripe } from '@stripe/stripe-js';
import TierBadge from '../components/subscription/TierBadge';
import { useAuth } from '../contexts/AuthContext';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
import './PaymentsPage.css';
import './SubscriptionPlans.css';

// Stripe singleton (mirrors PaymentsPage pattern)
let stripePromise;
if (STRIPE_ENABLED) {
  stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
}

// Timeout wrapper to prevent infinite spinning when services are down
const withTimeout = (promise, timeoutMs = 15000, operation = 'operation') => {
  return Promise.race([
    promise,
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error(`Request timeout: ${operation} took longer than ${timeoutMs}ms. The payments service may be unavailable.`)), timeoutMs)
    )
  ]);
};

/**
 * SubscriptionPage (simplified)
 * - 3-column plan showcase (subscribe or switch)
 * - Inline current subscription indicator ("Subscribed") instead of separate section
 * - Cancel / resume + billing portal actions surfaced near subscribed plan
 * - Auto-checkout support via ?plan=<alias>
 * - Lightweight polling after successful checkout
 * NOTE: Payment methods removed (handled directly in Stripe portal)
 */
export default function SubscriptionPage() {
  const paymentsEnabled = isPaymentsEnabled();
  const { user } = useAuth();
  const { subscription, refresh, startActivationPolling, pollTimeout } = useSubscription();
  const tier = getUserTier(subscription);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansFallback, setPlansFallback] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  
  // Initialize banner state from URL params or sessionStorage (persists across re-renders)
  const [banner, setBanner] = useState(() => {
    // Check sessionStorage first (in case component re-rendered)
    const storedBanner = sessionStorage.getItem('subscription_banner');
    if (storedBanner) {
      // Don't remove yet - let useEffect clean it up after render is stable
      return JSON.parse(storedBanner);
    }
    
    // Check URL params
    const params = new URLSearchParams(window.location.search);
    const hasCanceled = params.get('canceled');
    if (hasCanceled) {
      // Clean up URL immediately
      const url = new URL(window.location.href);
      url.searchParams.delete('canceled');
      window.history.replaceState({}, '', url.toString());
      // Store in sessionStorage in case of re-render
      const bannerData = { type: 'warn', text: 'Checkout canceled. You were not charged.' };
      sessionStorage.setItem('subscription_banner', JSON.stringify(bannerData));
      return bannerData;
    }
    return null;
  });

  // Clean up sessionStorage after component has stabilized
  useEffect(() => {
    if (sessionStorage.getItem('subscription_banner')) {
      // Use setTimeout to ensure cleanup happens after all renders
      const timer = setTimeout(() => {
        sessionStorage.removeItem('subscription_banner');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);
  const [updatingPlan, setUpdatingPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [resettingPaymentData, setResettingPaymentData] = useState(false);
  // Payment method management removed – actions handled via Stripe portal
  const [redirectingPlan, setRedirectingPlan] = useState(null);
  // Derived fields from context subscription (already normalized)
  const displaySub = subscription;
  const activePlanAlias = displaySub?.planAlias || null;
  const cancelAtPeriodEnd = displaySub?.cancelAtPeriodEnd;
  const currentPeriodEnd = displaySub?.currentPeriodEnd;
  
  // Find the actual matched plan name from the plans list - memoized to prevent recalculation
  const matchedPlan = useMemo(() => {
    return plans.find(p => {
      const subPriceId = displaySub?.priceId || displaySub?.items?.[0]?.price?.id || displaySub?.items?.[0]?.price || null;
      return (subPriceId && p.priceId && p.priceId === subPriceId) || (p.alias === activePlanAlias);
    });
  }, [plans, displaySub?.priceId, displaySub?.items, activePlanAlias]);
  
  const isAdmin = useMemo(() => user?.roles?.some(role => role.roleName === 'admin'), [user?.roles]);
  
  // Helper to get friendly display name for tier (including legacy)
  const getTierDisplayName = useCallback((tierString) => {
    if (!tierString) return null;
    const isLegacy = tierString.startsWith('legacy-');
    const baseTier = isLegacy ? tierString.replace('legacy-', '') : tierString;
    const tierLabels = {
      free: 'Casual',
      basic: 'Locked-In',
      pro: 'All-Gas',
      custom: 'Custom'
    };
    const label = tierLabels[baseTier] || baseTier;
    return isLegacy ? `${label} (Grandfathered)` : label;
  }, []);
  
  const currentPlanDisplayName = useMemo(() => 
    matchedPlan?.label || getTierDisplayName(tier) || activePlanAlias || (isFreeTier(subscription) ? 'Casual' : 'Paid Tier'),
    [matchedPlan?.label, tier, activePlanAlias, subscription, getTierDisplayName]
  );
  
  const formattedPeriodEnd = useMemo(() => {
    if (!currentPeriodEnd) return null;
    const ts = typeof currentPeriodEnd === 'number' ? currentPeriodEnd * 1000 : Date.parse(currentPeriodEnd);
    return ts ? new Date(ts).toLocaleString() : null;
  }, [currentPeriodEnd]);

  const money = useCallback((amt, cur='USD') => 
    typeof amt === 'number' ? (amt/100).toLocaleString(undefined,{ style:'currency', currency: cur }) : '', 
  []);

  // Reset all loading states on mount (cleanup after navigation)
  useEffect(() => {
    setRedirectingPlan(null);
    setUpdatingPlan(null);
    setCanceling(false);
    setProcessing(false);
    setResettingPaymentData(false);
  }, []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const serverPlans = await withTimeout(
        PaymentsServiceApi.listPlans(),
        10000,
        'Loading plans'
      );
      if (Array.isArray(serverPlans) && serverPlans.length) {
        console.log('[SubscriptionPage] Raw plans from API:', JSON.stringify(serverPlans, null, 2));
        const mappedPlans = serverPlans.map((p, index) => {
          const rawPriceId = p.priceId || p.price_id || null; // do NOT fallback to p.id to avoid duplicate matches
          const mapped = {
            alias: p.alias || p.planAlias || p.id || p.key || rawPriceId,
            label: p.label || p.name || (p.alias || p.planAlias || p.id || p.key || rawPriceId),
            amount: p.amount || p.unitAmount,
            currency: (p.currency || 'usd').toUpperCase(),
            interval: p.interval || p.recurringInterval || 'month',
            description: p.description || p.desc || '',
            priceId: rawPriceId,
            raw: p,
            _hasExplicitPriceId: !!rawPriceId
          };
          // Normalize known plan aliases/labels to match desktop LandingPage naming and prices
          try {
            const aliasKey = (mapped.alias || '').toString().toLowerCase();
            const labelKey = (mapped.label || '').toString().toLowerCase();
            // Free / Casual
            if (aliasKey === 'free' || labelKey === 'free' || aliasKey.includes('casual') || labelKey.includes('casual')) {
              mapped.label = 'Casual';
              mapped.amount = 0;
            }
            // Basic / Locked-In
            else if (aliasKey.includes('basic') || labelKey.includes('basic') || aliasKey.includes('locked') || labelKey.includes('locked')) {
              mapped.label = 'Locked-In';
              mapped.amount = 499;
            }
            // Pro / All-Gas
            else if (aliasKey.includes('pro') || labelKey.includes('pro') || aliasKey.includes('all') || labelKey.includes('all-gas') || labelKey.includes('allgas')) {
              mapped.label = 'All-Gas';
              mapped.amount = 1199;
            }
          } catch (e) {
            // if anything goes wrong, fall back to server-provided values
            console.warn('[SubscriptionPage] Plan normalization failed', e);
          }
          console.log(`[SubscriptionPage] Plan ${index}:`, {
            originalAlias: p.alias,
            originalPriceId: p.priceId,
            mappedAlias: mapped.alias,
            mappedPriceId: mapped.priceId,
            mappedLabel: mapped.label
          });
          return mapped;
        });
        console.log('[SubscriptionPage] Mapped plans:', JSON.stringify(mappedPlans, null, 2));
        setPlans(mappedPlans);
      } else throw new Error('Empty plans');
    } catch {
      setPlansFallback(true);
      setPlans([
        { alias:'basic', label:'Locked-In', amount:499, interval:'month', currency:'USD', description:'All organization and planning features from the Casual plan', priceId:'basic' },
        { alias:'pro', label:'All-Gas', amount:1199, interval:'month', currency:'USD', description:'All planning and AI study tools from the Locked-In plan', priceId:'pro' }
      ]);
    } finally { setPlansLoading(false); }
  }, []);

  // Load plans once on mount
  useEffect(() => {
    if (paymentsEnabled) {
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsEnabled]);

  // Banner detection (success / canceled / portal) - run once on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      setBanner({ type: 'success', text: 'Subscription activated! Welcome.' });
      UsageEventsTracker.recordUserAction('SUBSCRIPTION_CHECKOUT_SUCCESS', {
        action: 'complete',
        category: 'subscription',
        label: 'checkout_success',
        metadata: { source: 'subscription_page' }
      });
      startActivationPolling();
    } else if (params.get('payment_success')) {
      setBanner({ type: 'success', text: 'Payment confirmed! Your plan has been updated.' });
      startActivationPolling();
      // Clean up URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('payment_success');
      window.history.replaceState({}, '', url.toString());
    } else if (params.get('payment_canceled')) {
      setBanner({ type: 'warn', text: 'Payment canceled. Your plan was not changed.' });
      UsageEventsTracker.recordUserAction('SUBSCRIPTION_CHECKOUT_CANCELED', {
        action: 'cancel',
        category: 'subscription',
        label: 'checkout_canceled',
        metadata: { source: 'subscription_page' }
      });
      // Clean up URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('payment_canceled');
      window.history.replaceState({}, '', url.toString());
    } else if (params.get('portal')) {
      setBanner({ type:'info', text: 'Returned from billing portal. Recent changes may take a moment to reflect.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount, don't re-run when startActivationPolling changes

  // Detect incomplete subscription and show warning banner
  useEffect(() => {
    // Only set banner if we have subscription data and it's incomplete
    // AND we don't already have a banner (don't override success/canceled messages)
    if (!banner && (displaySub?.status === 'incomplete' || displaySub?.status === 'incomplete_expired')) {
      const targetPlan = plans.find(p => p.priceId === displaySub?.priceId || p.alias === displaySub?.planAlias);
      const planName = targetPlan?.label || displaySub?.planAlias || 'selected plan';
      setBanner({ 
        type: 'warn', 
        text: `Payment incomplete for ${planName}. Please complete checkout to activate your subscription.` 
      });
    }
  }, [displaySub?.status, displaySub?.priceId, displaySub?.planAlias, plans, banner]);

  // Polling interval cleanup now fully handled in SubscriptionContext; no local cleanup required.

  const handleCheckout = useCallback(async (planAlias) => {
    if (!STRIPE_ENABLED) { setActionMsg('Stripe disabled.'); return; }
    console.log('[SubscriptionPage] handleCheckout called with planAlias:', planAlias);
    setRedirectingPlan(planAlias); setActionMsg(null);
    const corrId = UsageEventsTracker.startCorrelation('checkout');
    UsageEventsTracker.recordUserAction('SUBSCRIPTION_CHECKOUT_STARTED', {
      action: 'start',
      category: 'subscription',
      label: 'checkout_started',
      metadata: { planAlias, source: 'subscription_page' }
    }, { correlationId: corrId });
    try {
      if (!stripePromise) throw new Error('Stripe not loaded');
      // Robust idempotency key: attempt crypto.randomUUID() else fallback
      const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `checkout-${crypto.randomUUID()}`
        : `checkout-${planAlias}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      // Redirect to billing page on success, back to subscription page on cancel
      const successUrl = `${window.location.origin}/billing?success=1`;
      const cancelUrl = `${window.location.origin}/subscription?canceled=1`;
      console.log('[SubscriptionPage] Creating checkout session with:', { planAlias, successUrl, cancelUrl, idempotencyKey });
      const data = await withTimeout(
        PaymentsServiceApi.createCheckoutSession({ planAlias, successUrl, cancelUrl, idempotencyKey }),
        15000,
        'Creating checkout session'
      );
      console.log('[SubscriptionPage] Checkout session response:', data);
      if (data?.sessionId) {
        const stripe = await stripePromise; const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId }); if (error) throw error;
      } else if (data?.url) {
        window.location.href = data.url;
      } else throw new Error('Checkout response missing sessionId or url');
    } catch(e) {
      // Structured error handling
      const errorMsg = e.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setActionMsg('Payment service is currently unavailable. Please try again in a few moments.');
      } else if (errorMsg.includes('SUBSCRIPTION_CREATE_FAILED')) {
        setActionMsg('Failed to create subscription. Please try again or contact support.');
      } else if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        setActionMsg('Session expired. Please log in again.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else {
        setActionMsg(errorMsg || 'Checkout failed. Please try again.');
      }
    } finally {
      setRedirectingPlan(null);
    }
  }, []);

  // Auto-checkout if redirected with ?plan=<alias> and no subscription yet
  useEffect(() => {
    if (!paymentsEnabled || !STRIPE_ENABLED || plansLoading || redirectingPlan) return;
    
    const params = new URLSearchParams(window.location.search);
    const preselect = params.get('plan');
    if (!preselect) return;
    
    if (displaySub?.subscriptionId) { 
      // clean plan param if already subscribed
      const url = new URL(window.location.href); 
      url.searchParams.delete('plan'); 
      window.history.replaceState({},'',url.toString());
      return;
    }
    
    const match = plans.find(p => p.alias === preselect);
    if (match) {
      // Slight delay for UX before redirecting
      const timer = setTimeout(() => handleCheckout(match.alias), 400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoading, redirectingPlan, displaySub?.subscriptionId]); // Only depend on what actually changes

  // Mirror subscription hydration now centralized; legacy mirror fetch effect removed.

  const handleCancel = async (immediate = false) => {
    setCanceling(true); setActionMsg(null); setShowCancelModal(false);
    const corrId = UsageEventsTracker.startCorrelation('cancel_sub');
    UsageEventsTracker.recordUserAction('SUBSCRIPTION_CANCEL_STARTED', {
      action: 'start',
      category: 'subscription',
      label: immediate ? 'cancel_immediate' : 'cancel_at_period_end',
      metadata: { immediate, source: 'subscription_page' }
    }, { correlationId: corrId });
    try {
      await withTimeout(
        PaymentsServiceApi.cancelSubscription(!immediate),
        15000,
        'Canceling subscription'
      );
      const msg = immediate 
        ? 'Subscription canceled immediately. You are now on the free tier.'
        : 'Subscription will cancel at period end. You\'ll retain access until then.';
      UsageEventsTracker.recordUserAction('SUBSCRIPTION_CANCEL_SUCCESS', {
        action: 'complete',
        category: 'subscription',
        label: immediate ? 'cancel_immediate' : 'cancel_at_period_end',
        metadata: { immediate, source: 'subscription_page' }
      }, { correlationId: UsageEventsTracker.getCorrelation('cancel_sub') });
      UsageEventsTracker.clearCorrelation('cancel_sub');
      setActionMsg(msg);
      await refresh();
    } catch(e) {
      // Structured error handling
      const errorMsg = e.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setActionMsg('Payment service is currently unavailable. Please try again in a few moments.');
      } else if (errorMsg.includes('404') || errorMsg.includes('not found') || errorMsg.includes('SUBSCRIPTION_NOT_FOUND')) {
        setActionMsg('No active subscription found. Please refresh the page.');
      } else if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        setActionMsg('Session expired. Please log in again.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else {
        setActionMsg(errorMsg || 'Failed to cancel subscription. Please try again.');
      }
    } finally {
      setCanceling(false);
    }
  };

  const handleResume = async () => {
    setProcessing(true); setActionMsg(null);
    const corrId = UsageEventsTracker.startCorrelation('resume_sub');
    UsageEventsTracker.recordUserAction('SUBSCRIPTION_RESUME_STARTED', {
      action: 'start',
      category: 'subscription',
      label: 'resume',
      metadata: { source: 'subscription_page' }
    }, { correlationId: corrId });
    try { 
      await withTimeout(
        PaymentsServiceApi.resumeSubscription(),
        15000,
        'Resuming subscription'
      ); 
      setActionMsg('Cancellation removed.'); 
      await refresh(); 
    }
    catch(e){ 
      const errorMsg = e.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setActionMsg('Payment service is currently unavailable. Please try again in a few moments.');
      } else {
        setActionMsg(errorMsg || 'Failed to resume.');
      }
    }
    finally { setProcessing(false); }
  };

  const handleUpdatePlan = async (alias) => {
    if (!alias) return;
    
    // Special case: If user already has an incomplete subscription for this plan,
    // skip modify-subscription and go directly to checkout
    // Match by either planAlias or priceId since displaySub.planAlias might be the priceId
    const incompleteStatus = displaySub?.status === 'incomplete' || displaySub?.status === 'incomplete_expired';
    const matchingPlan = plans.find(p => 
      p.alias === alias || 
      (p.priceId && (p.priceId === displaySub?.priceId || p.priceId === displaySub?.planAlias))
    );
    const hasIncompleteSub = incompleteStatus && matchingPlan && 
                              (displaySub?.planAlias === alias || 
                               displaySub?.planAlias === matchingPlan.priceId || 
                               displaySub?.priceId === matchingPlan.priceId);
    
    if (hasIncompleteSub) {
      // Directly create checkout session for incomplete subscription
      // Use the plan's alias (not priceId) for the checkout session
      const correctAlias = matchingPlan.alias;
      setUpdatingPlan(correctAlias); setActionMsg(null);
      setActionMsg('Redirecting to complete your payment...');
      setRedirectingPlan(correctAlias);
      
      try {
        if (!STRIPE_ENABLED || !stripePromise) {
          throw new Error('Stripe not available');
        }
        
        const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? `complete-${crypto.randomUUID()}`
          : `complete-${correctAlias}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        
        const successUrl = `${window.location.origin}/subscription?payment_success=1`;
        const cancelUrl = `${window.location.origin}/subscription?payment_canceled=1`;
        
        const checkoutData = await withTimeout(
          PaymentsServiceApi.createCheckoutSession({ 
            planAlias: correctAlias, 
            successUrl, 
            cancelUrl, 
            idempotencyKey 
          }),
          15000,
          'Creating checkout session'
        );
        
        if (checkoutData?.sessionId) {
          const stripe = await stripePromise;
          const { error } = await stripe.redirectToCheckout({ sessionId: checkoutData.sessionId });
          if (error) throw error;
        } else if (checkoutData?.url) {
          window.location.href = checkoutData.url;
        } else {
          throw new Error('Checkout session creation failed');
        }
      } catch (e) {
        setActionMsg(e.message || 'Failed to open checkout. Please try again.');
      } finally {
        setUpdatingPlan(null);
        setRedirectingPlan(null);
      }
      return;
    }
    
    // Normal case: changing plans or first-time upgrade
    if (alias === activePlanAlias) return;
    setUpdatingPlan(alias); setActionMsg(null);
    try {
      // Use priceKey (alias) with new modifySubscription endpoint
      const result = await withTimeout(
        PaymentsServiceApi.modifySubscription(alias),
        15000,
        'Modifying subscription'
      );
      
      // Check if payment confirmation is needed (subscription status incomplete with clientSecret)
      // This happens when:
      // - User has no payment method on file (new subscriber, previously canceled, archived price)
      // - Payment method failed/declined
      // - Additional authentication required (3D Secure)
      if (result.subscription?.status === 'incomplete' && result.clientSecret) {
        if (!STRIPE_ENABLED || !stripePromise) {
          throw new Error('Stripe not available for payment confirmation');
        }
        
        // Redirect to Stripe Checkout to collect/confirm payment method
        // This handles all scenarios: new users, archived prices, canceled users re-subscribing
        setActionMsg('Payment confirmation required. Redirecting to secure checkout...');
        setRedirectingPlan(alias);
        
        try {
          // Create a new checkout session for this incomplete subscription
          // Use robust idempotency key
          const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? `modify-${crypto.randomUUID()}`
            : `modify-${alias}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
          
          const successUrl = `${window.location.origin}/subscription?payment_success=1`;
          const cancelUrl = `${window.location.origin}/subscription?payment_canceled=1`;
          
          const checkoutData = await withTimeout(
            PaymentsServiceApi.createCheckoutSession({ 
              planAlias: alias, 
              successUrl, 
              cancelUrl, 
              idempotencyKey 
            }),
            15000,
            'Creating checkout session'
          );
          
          if (checkoutData?.sessionId) {
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId: checkoutData.sessionId });
            if (error) throw error;
          } else if (checkoutData?.url) {
            window.location.href = checkoutData.url;
          } else {
            throw new Error('Checkout session creation failed - no session ID or URL returned');
          }
        } catch (checkoutError) {
          // If checkout creation fails, provide clear guidance
          const checkoutMsg = checkoutError.message || '';
          if (checkoutMsg.includes('session') || checkoutMsg.includes('checkout')) {
            throw new Error('Unable to create payment session. Please try again or contact support.');
          }
          throw checkoutError;
        }
        return; // Exit early - redirect happening
      } else {
        // No payment confirmation needed, subscription updated immediately (proration handled by backend)
        const msg = result?.message || 'Plan updated successfully. Proration has been applied.';
        setActionMsg(msg);
        await refresh();
      }
    } catch(e) {
      // Structured error handling
      const errorMsg = e.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setActionMsg('Payment service is currently unavailable. Please try again in a few moments.');
      } else if (errorMsg.includes('UNKNOWN_PLAN_ALIAS') || errorMsg.includes('Unknown plan')) {
        setActionMsg('Invalid plan selected. Please try a different plan.');
      } else if (errorMsg.includes('SUBSCRIPTION_NOT_FOUND') || errorMsg.includes('not found')) {
        setActionMsg('No active subscription found. Please subscribe first.');
      } else if (errorMsg.includes('payment method') || errorMsg.includes('Payment method')) {
        setActionMsg(errorMsg);
      } else if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        setActionMsg('Session expired. Please log in again.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else {
        setActionMsg(errorMsg || 'Failed to update plan. Please try again.');
      }
    } finally {
      setUpdatingPlan(null);
      setRedirectingPlan(null);
    }
  };

  // Admin only: Reset payment data for testing
  const handleResetPaymentData = async () => {
    if (!isAdmin) return;
    
    const confirmReset = window.confirm(
      'This will permanently delete all payment data for this user from the payments service. '
      + 'This action cannot be undone. Are you sure you want to continue?'
    );
    
    if (!confirmReset) return;
    
    setResettingPaymentData(true);
    try {
      const userId = localStorage.getItem('user_id');
      const response = await withTimeout(
        PaymentsApi.deletePaymentData(userId),
        15000,
        'Deleting payment data'
      );
      
      if (response.ok) {
        setBanner({ type: 'success', msg: 'Payment data has been reset successfully. Refreshing...' });
        setTimeout(() => {
          refresh();
          window.location.reload();
        }, 1500);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setBanner({ type: 'warn', msg: `Failed to reset payment data: ${errorData.message || response.statusText}` });
      }
    } catch (error) {
      console.error('Error resetting payment data:', error);
      const errorMsg = error.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setBanner({ type: 'warn', msg: 'Service is currently unavailable. Please try again in a few moments.' });
      } else {
        setBanner({ type: 'warn', msg: `Error resetting payment data: ${errorMsg}` });
      }
    } finally {
      setResettingPaymentData(false);
    }
  };

  const handlePortal = async () => {
    setProcessing(true); setActionMsg(null);
    try { 
      const data = await withTimeout(
        PaymentsServiceApi.createPortalSession(window.location.origin + '/subscription?portal=1'),
        15000,
        'Opening billing portal'
      ); 
      if (data?.url) window.location.href = data.url; 
      else setActionMsg('Failed to open billing portal.'); 
    }
    catch(e){ 
      const errorMsg = e.message || '';
      if (errorMsg.includes('timeout') || errorMsg.includes('unavailable')) {
        setActionMsg('Payment service is currently unavailable. Please try again in a few moments.');
      } else {
        setActionMsg(errorMsg || 'Portal error.');
      }
    }
    finally { setProcessing(false); }
  };

  // Payment method handlers removed

  if (!paymentsEnabled) {
    return (
      <div className="payments-page" data-testid="subscription-page">
        <h1 className="pay-title">Subscription Management</h1>
        <div className="pay-banner warn">Payments feature disabled.</div>
      </div>
    );
  }

  return (
    <div className="payments-page" data-testid="subscription-page">
      <div className="subscription-container">
        {/* Compact Header with inline current plan */}
        <div className="subscription-header-compact">
          <div className="subscription-header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
              <h1 className="subscription-title-compact">Subscription Management</h1>
              <TierBadge tier={tier} data-tour="current-tier" />
            </div>
            <p className="subscription-subtitle-compact">
              Manage your plan, billing, and premium features in one place.
            </p>
          </div>
          <div className="subscription-header-right">
            <div className="current-plan-pill">
              <span className="current-plan-pill-label">Current plan:</span>
              <span className="current-plan-pill-value">{currentPlanDisplayName}</span>
            </div>
            {formattedPeriodEnd && isPaidTier(subscription) && (
              <div className="current-plan-renewal">
                {cancelAtPeriodEnd ? `Ends ${formattedPeriodEnd}` : `Renews ${formattedPeriodEnd}`}
              </div>
            )}
            {(displaySub?.status === 'incomplete' || displaySub?.status === 'incomplete_expired') && (() => {
              const matchingPlan = plans.find(p => 
                p.priceId === displaySub?.priceId || 
                p.priceId === displaySub?.planAlias ||
                p.alias === displaySub?.planAlias
              );
              
              if (!matchingPlan) return null;
              
              return (
                <button 
                  className="btn-primary" 
                  style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem', marginTop: '0.5rem' }}
                  onClick={() => handleUpdatePlan(matchingPlan.alias)}
                  disabled={updatingPlan || redirectingPlan}
                >
                  {updatingPlan === matchingPlan.alias ? 'Processing...' : 'Complete Payment'}
                </button>
              );
            })()}
          </div>
        </div>
      
      {banner && <div className={`pay-banner ${banner.type}`}>{banner.text}</div>}
      {pollTimeout && !displaySub?.subscriptionId && <div className="pay-banner warn">Still finalizing subscription – refresh later or contact support.</div>}
      {plansFallback && <div className="pay-banner warn" style={{marginBottom:'12px'}}>Using fallback plan list.</div>}
      
      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>How would you like to cancel your subscription?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
              <button 
                className="btn-secondary" 
                onClick={() => handleCancel(false)}
                disabled={canceling}
              >
                Cancel at Period End
              </button>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0' }}>
                You'll retain access until {formattedPeriodEnd || 'the end of your billing period'}.
              </p>
              <button 
                className="btn-outline" 
                onClick={() => setShowCancelModal(false)}
                disabled={canceling}
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}

        <div className="plans-showcase-compact">
          <h2 className="plans-section-title-compact">Choose a Plan</h2>
          {plansLoading ? <div className="pay-muted">Loading plans…</div> : (
            <div className="plan-grid-compact" data-tour="tiers">
              {/* Free / Casual Tier Card */}
              <div className={`plan-card-compact premium-pricing-card ${isFreeTier(subscription) && !displaySub?.subscriptionId ? 'active' : ''}`} data-testid="plan-free">
                <img 
                  className="pricingCard__mascot" 
                  src="/scout_images/Scout_Thumbsup.png" 
                  alt="Scout mascot for Casual plan" 
                />
                <div className="premium-card-header">
                  <h3 className="premium-tier-name">Casual</h3>
                  <p className="premium-identity-line">For staying organized without friction</p>
                </div>

                <div className="premium-price-section">
                  <div className="premium-price">$0<span className="premium-price-period"> / month</span></div>
                </div>

                <div className="premium-divider"></div>

                <ul className="premium-features-list">
                  <li><span className="premium-check">✓</span>Syllabus upload with automatic calendar syncing and deadline timeline</li>
                  <li><span className="premium-check">✓</span>Assignments and class events automatically organized by course</li>
                  <li><span className="premium-check">✓</span>Course materials stored and grouped by class</li>
                  <li><span className="premium-check">✓</span>Central dashboard to view classes, deadlines, and progress</li>
                </ul>

                <p className="premium-best-for">Students who want structure without overthinking it.</p>

                <div className="plan-card-actions-compact" style={{ paddingTop: '1rem' }}>
                  {isFreeTier(subscription) && !displaySub?.subscriptionId ? (
                    <button className="btn-disabled" disabled>Current Plan</button>
                  ) : displaySub?.subscriptionId ? (
                    <button className="btn-danger" disabled={canceling} onClick={() => setShowCancelModal(true)}>
                      {canceling ? 'Canceling…' : 'Downgrade to Free'}
                    </button>
                  ) : (
                    <button className="btn-disabled" disabled>Current Plan</button>
                  )}
                </div>
              </div>
              {(() => {
                // Precompute duplicate priceId counts to avoid marking multiple plans active.
                const priceIdCounts = plans.reduce((acc, pl) => { if (pl.priceId) acc[pl.priceId] = (acc[pl.priceId] || 0) + 1; return acc; }, {});
                const subPriceId = displaySub?.priceId || displaySub?.items?.[0]?.price?.id || displaySub?.items?.[0]?.price || null;
                const subAlias = activePlanAlias || null;
                let activePriceIdConsumed = false; // ensure only one plan gets active when multiple share same priceId
                return plans.map((p, idx) => {
                  const duplicatePrice = p.priceId && priceIdCounts[p.priceId] > 1;
                  let active = false;
                  let usedCriterion = null;
                  if (subPriceId && p.priceId && p.priceId === subPriceId) {
                    if (!duplicatePrice || (duplicatePrice && !activePriceIdConsumed)) {
                      active = true; usedCriterion = 'priceId'; activePriceIdConsumed = true;
                    }
                  } else if (!subPriceId && p.priceId == null && subAlias && p.alias === subAlias) {
                    active = true; usedCriterion = 'alias';
                  }
                  if (active) {
                    console.log('[SubscriptionPage] Active match details', {
                      planAlias: p.alias,
                      planPriceId: p.priceId,
                      subPriceId,
                      subAlias,
                      duplicatePrice,
                      usedCriterion
                    });
                  }
                const params = new URLSearchParams(window.location.search);
                const preselect = params.get('plan');
                const preselected = !displaySub?.subscriptionId && preselect && (preselect === p.alias);
                
                // Determine features based on plan alias/label
                const isBasicPlan = p.alias?.toLowerCase().includes('basic') || p.label?.toLowerCase().includes('basic');
                const isProPlan = p.alias?.toLowerCase().includes('pro') || p.label?.toLowerCase().includes('pro');
                
                let features = [];
                if (isBasicPlan) {
                  features = [
                      '✅ All organization and planning features from the Casual plan',
                      '✅ AI-generated quizzes from your course materials',
                      '✅ AI-generated flashcards from your course materials',
                      '✅ Fast review and study tools built from what you upload',
                      `${p.interval.charAt(0).toUpperCase()+p.interval.slice(1)} billing`
                    ];
                } else if (isProPlan) {
                    features = [
                      '✅ All planning and AI study tools from the Locked-In plan',
                      '✅ Audiobooks generated from your course materials',
                      '✅ AI-generated study podcasts from your classes',
                      '✅ Learn hands-free while commuting, training, or walking',
                      `${p.interval.charAt(0).toUpperCase()+p.interval.slice(1)} billing`
                    ];
                } else {
                  // Fallback for custom plans
                  features = [
                    '✅ Premium features',
                    `${p.interval.charAt(0).toUpperCase()+p.interval.slice(1)} billing`,
                    p.description || 'Priority support'
                  ];
                }
                
                // Build identity and best-for lines to match LandingPage copy
                const identityLine = p.label === 'Casual' ? 'For staying organized without friction' : p.label === 'Locked-In' ? "For students who don't miss deadlines" : p.label === 'All-Gas' ? 'For total immersion and zero excuses' : (p.description || '');
                const bestForText = p.label === 'Casual' ? 'Students who want structure without overthinking it.' : p.label === 'Locked-In' ? 'Students who are serious about grades and consistency.' : p.label === 'All-Gas' ? 'Students who want every possible edge.' : '';
                const mascotSrc = p.label === 'Casual' ? '/scout_images/Scout_Thumbsup.png' : p.label === 'Locked-In' ? '/scout_images/Scout_Mid.png' : '/scout_images/Scout_Extreme.png';

                return (
                  <div key={p.alias} className={`plan-card-compact premium-pricing-card ${active?'active':''} ${preselected?'preselected':''}`} data-testid={`plan-${p.alias}`}>
                    {p.label === 'All-Gas' && (
                      <div className="premium-most-popular-badge">Most Popular</div>
                    )}

                    <img 
                      className="pricingCard__mascot" 
                      src={mascotSrc} 
                      alt={`Scout mascot for ${p.label} plan`} 
                    />

                    <div className="premium-card-header">
                      <h3 className="premium-tier-name">{p.label}</h3>
                      <p className="premium-identity-line">{identityLine}</p>
                    </div>

                    <div className="premium-price-section">
                      <div className="premium-price">{typeof p.amount === 'number' ? money(p.amount, p.currency) : '$0.00'}<span className="premium-price-period"> / {p.interval}</span></div>
                    </div>

                    <div className="premium-divider"></div>

                    <ul className="premium-features-list">
                      {features.filter(f => !f.includes('billing')).map((feature, i) => (
                        <li key={i}><span className="premium-check">✓</span>{feature.replace(/^✅\s*/, ' ')}</li>
                      ))}
                    </ul>

                    <p className="premium-best-for">{bestForText}</p>

                    <div className="plan-card-actions-compact" style={{ paddingTop: '1rem' }}>
                      {displaySub?.subscriptionId ? (
                        active ? (
                          <>
                            <button className="btn-disabled" disabled>Subscribed</button>
                            <div className="sub-action-row">
                              {!cancelAtPeriodEnd ? (
                                <button className="btn-danger" disabled={canceling} onClick={() => setShowCancelModal(true)}>Cancel Subscription</button>
                              ) : (
                                <button className="btn-primary" disabled={processing} onClick={handleResume}>{processing?'Resuming…':'Resume'}</button>
                              )}
                              <button className="btn-outline" disabled={processing} onClick={handlePortal}>{processing?'Opening…':'Billing Portal'}</button>
                            </div>
                          </>
                        ) : (
                          <button className="btn-primary" disabled={updatingPlan===p.alias} onClick={()=>handleUpdatePlan(p.alias)}>{updatingPlan===p.alias?'Updating…':'Switch'}</button>
                        )
                      ) : (
                        <button className="btn-primary" disabled={redirectingPlan===p.alias} onClick={()=>handleCheckout(p.alias)}>{redirectingPlan===p.alias?'Redirecting…':'Subscribe Now'}</button>
                      )}
                    </div>
                  </div>
                );
                });
              })()}
            </div>
          )}
          {actionMsg && <div className="pay-status" style={{marginTop:'12px'}}>{actionMsg}</div>}
        </div>
        
        {/* Subscription Trust Strip */}
        <div className="subscriptionTrustStripWrap">
          <div className="subscriptionTrustStrip">
            <div className="subscriptionTrustIcon" aria-hidden="true">🛡️</div>
            <div className="subscriptionTrustText">
              <p className="subscriptionTrustPrimary">Cancel anytime. No contracts. No pressure.</p>
              <p className="subscriptionTrustSecondary">You are always in control of your subscription.</p>
              <p className="subscriptionTrustTertiary">Upgrade, downgrade, or cancel anytime from your account settings.</p>
            </div>
          </div>
        </div>
        
        {/* Admin only: Reset payment data button */}
        {isAdmin && (
          <section className="admin-section-compact">
            <div style={{maxWidth: '600px', margin: '0 auto', textAlign: 'center'}}>
              <h3 style={{fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem'}}>Admin Testing Tools</h3>
              <p style={{fontSize: '0.75rem', marginBottom: '1rem', opacity: 0.7}}>
                Danger zone: These actions are irreversible
              </p>
              <button 
                onClick={handleResetPaymentData}
                disabled={resettingPaymentData}
                className="btn-danger"
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  opacity: resettingPaymentData ? 0.6 : 1
                }}
              >
                {resettingPaymentData ? 'Resetting...' : '🗑️ Reset Payment Data'}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
